import {
  AfterViewInit,
  ElementRef,
  EventEmitter,
  inject,
  Inject,
  Injectable,
  Injector,
  OnDestroy,
  OnInit
} from "@angular/core";
import {ReplaySubject, Subject, Subscription} from "rxjs";
import $ from 'jquery';
import {
  camelCase,
  forEach,
  isArray,
  isFunction,
  isString,
  isObject,
  get,
  join,
  last,
  map,
  set,
  startsWith,
  extend,
  isEmpty
} from 'lodash-es';
import {
  $invokeWatchers,
  $parseEvent,
  $unwatch,
  $watch,
  addClass, App,
  getDataSource,
  isDefined,
  isMobileApp,
  removeAttr,
  removeClass,
  runtimeEventManager,
  setAttr,
  setCSS,
  setCSSFromObj,
  switchClass
} from '@wm/core';

import { ChangeListener, Context, IWidgetConfig, WidgetConfig } from "../../framework/types";
import { DISPLAY_TYPE, EVENTS_MAP , TOKENS_KEYS} from "../../framework/constants";
import { EventManager } from "@angular/platform-browser";
import { WidgetProxyProvider } from "../../framework/widget-proxy.provider";
import { getWidgetPropsByType } from "../../framework/widget-props";
import { getWidgetRegistry, register, renameWidget } from '../../framework/widget-registry';
import { widgetIdGenerator } from "../../framework/widget-id-generator";
import { isStyle } from '../../framework/styler';
import { getWatchIdentifier } from '../../../utils/widget-utils';
import { Parent } from '../../framework/parent.provider';
// Gets list of classes to add and remove and applies on the $el
const updateClasses = (toAdd: Array<string>, toRemove: Array<string>, el: HTMLElement) => {
  if (toRemove && toRemove.length) {
    removeClass(el, join(toRemove, ' '));
  }
  if (toAdd && toAdd.length) {
    addClass(el, join(toAdd, ' '));
  }
};

// To add and remove styles on the $el
const updateStyles = (nv: any, ov: any, el: HTMLElement) => {
  if (ov && isObject(ov)) {
    const keys = Object.keys(ov || {});
    keys.forEach(function (key) {
      setCSS(el, key, '');
    });
  }
  if (nv && isObject(nv)) {
    setCSSFromObj(el, nv);
  }

};

const MapWidgetProp: {[key: string]: string} = {
  'widget': 'displaywidget'
}

const PAGE_STRUCTURE_MAP_FOR_WIDGETS: Map<string, Array<string>> = new Map([
    ['wm-accordion', ['type']],
    ['wm-tabs', ['type']]
  ]);

@Injectable()
export abstract class BaseComponent implements OnInit, AfterViewInit, OnDestroy {
  // DOM node reference of the component root
  protected readonly nativeElement: HTMLElement;
  // Type of the component
  public readonly widgetType: string;
  // Most of the cases it is same as widgetType
  // for specific widgets like charts widgetType can be wm-chart whereas the subtype can be wm-bar-chart
  protected readonly widgetSubType: string;
  // Proxy for the component instance.
  public widget: any;
  // View parent component
  // eg: Page, Partial, Prefab
  // protected viewParent: any;
  // To add/remove events
  protected readonly eventManager: EventManager;
  // context of the widget
  // when the widget is preset inside a repeater this context will have the repeater related properties
  public context: any;
  public widgetProps: { [key: string]: any };
  // Display type of the component. eg, block(Default), inline-block, inline etc
  private readonly displayType: string;
  // Property change subject and observable
  private readonly propertyChange = new Subject();
  /**
   * Internal flag to determine whether to wait for the widget initialization or not
   * If the initPromise is provided in the construction wait till the promise is resolved
   * If the initPromise is not provided proceed with the initialization, which is the default behavior.
   */
  private readonly delayedInit: boolean;
  /**
   * Initial state of the widget.
   * Will be undefined once the initial properties are set on to the component
   */
  private initState: Map<string, any> | undefined;
  public $attrs = new Map<string, string>();
  // On Ready State change subject and observable
  private readonly readyState = new Subject();
  //Style change subject and observable
  private readonly styleChange = new ReplaySubject();
  // Component destroy subject and observable
  private readonly destroy = new Subject();
  // Map of event handler callbacks
  private eventHandlers = new Map<string, { callback: Function, locals: any }>();
  private isMuted = false;
  private _initialized = false;
  private DEFAULT_CLS: string;

  public eventEmitter = new EventEmitter();
  /**
   * Holds the event registration functions.
   * these functions needs to be executed after onViewInit
   */
  private toBeSetupEventsQueue: Array<Function> = [];
  private baseVariableService: any;
  private locale: any;
  EVENT: any = 'event';
  private evtSubscriptions = new Subscription();
  private i18nService: any;
  private pageName: string = '';
  customWidgetSubType: string;
  private widgetStylingService: any;
  private widgetProperties: any;

  protected constructor(
    protected inj: Injector,
    @Inject(WidgetConfig) config: IWidgetConfig,
    public viewParent?: Parent,
    initPromise?: Promise<any>
  ) {
    const elementRef = inject(ElementRef);
    this.nativeElement = elementRef.nativeElement;
    this.widgetType = config.widgetType || this.nativeElement.getAttribute('widgettype') || '';
    this.widgetSubType = config.widgetSubType || config.widgetType;
    if (this.nativeElement?.parentElement?.hasAttribute('data-col-identifier') && !this.nativeElement.getAttribute('name')) {
      this.nativeElement.setAttribute('name', this.widgetType.replace('wm-', ''));
    }
    this.displayType = config.displayType || DISPLAY_TYPE.BLOCK;
    this.context = this; // TODO: test waht is context
    this.widget = this.createProxy();
    this.eventManager = inject(EventManager);
    this.baseVariableService = runtimeEventManager.getService('variableService');
    this.pageName = this.baseVariableService.pageService.activePageName;

    (this.nativeElement as any).widget = this.widget;

    this.i18nService = runtimeEventManager.getService('i18Services');
    this.widgetStylingService = runtimeEventManager.getService("widgetStylingService");
    this.widgetProperties = runtimeEventManager.getService("widgetPropertiesProvider");

    this.initContext();

    if (config.hostClass) {
      this.DEFAULT_CLS = config.hostClass;
      addClass(this.nativeElement, config.hostClass);
    }

    // register default property change handler and style change handler
    this.registerStyleChangeListener(this.onStyleChange, this);
    this.registerPropertyChangeListener(this.onPropertyChange, this);
    this.registerReadyStateListener(() => {
      this._initialized = true;
      this.updatePageStructure();
    });

    // if the initPromise is provided, wait till the promise is resolved to proceed with the widget initialization
    if (!initPromise) {
      this.initWidget();
    } else {
      this.delayedInit = true;
      initPromise.then((resolveFn) => {
        let formFieldCW = this.widgetSubType === 'wm-form-field-custom-widget',
            cw          = this.widgetType.startsWith('wm-custom-');
        if( formFieldCW || cw ) {
          // enabling the below commented line makes the custom widget to inherit the base widget properties that is specified as widgetType in the config.json of a custom widget
          // @ts-ignore
          this.customWidgetSubType = formFieldCW ? 'wm-form-field-' + last(this["formWidget"].config.widgetType.split('-')) : this["config"].widgetType;
        }
        this.initWidget();
        this.setInitProps();
        resolveFn && resolveFn()
      });
    }
  }

  get appLocale() {
    return this.i18nService.getMessages() || this.i18nService.appLocale;
  }

  public getAttr(attrName: any): string | undefined {
    return this.$attrs.get(attrName);
  }

  get widgetId() {
    return this.nativeElement.getAttribute('widgetid');
  }

  get tokenFileName(): string {
    const getSpecialTokenFileName = (widget: any, widgetType: any) => {
      // Toggle widget uses 'checkbox' internally but needs 'toggle' token file
      if (widgetType === 'wm-checkbox' && widget?.type === 'toggle') {
        return 'toggle';
      }else if(widgetType === 'wm-checkbox' && widget?.type === 'button'){
        return 'iconbutton-toggleable';
      }
      return null;
    };

    // for form-field, get tokenFileName from the form field display widget type
    if (this.widgetType === 'wm-form-field') {
      const displayWidget = (this as any).displaywidget;
      const selector = `[wm${displayWidget}]`;

      const fieldWidget = this.$element.find(selector).get(0);

      if (fieldWidget?.widget) {
        const { widget } = fieldWidget;
        const widgetType = widget.widgetType;

        return (
          getSpecialTokenFileName(widget, widgetType) ||
          widget.widgetProperties?.getConfig(widgetType)?.tokenFileName
        );
      }
    }

    return (
      getSpecialTokenFileName(this.widget, this.widgetType) ||
      this.widgetProperties?.getConfig(this.widgetType)?.tokenFileName
    );
  }

  get Variables() {
    let prefab: string = get((this as any), 'prefabName');
    return this.baseVariableService.getVariableFromNameSpace(prefab);
  }

  protected createProxy(): any {
    return WidgetProxyProvider.create(this, this.widgetSubType, getWidgetPropsByType(this.widgetSubType));
  }

  /**
   * jQuery nativeElement reference of the component root
   */
  public get $element(): any {
    const el = $(this.nativeElement);
    return el;
  }

  get stylerEl(): HTMLElement | null {
    return null;
  }

  public getNativeElement(): HTMLElement {
    return this.nativeElement;
  }

  public getWidgetType(): string {
    return this.widgetType;
  }

  public getWidgetSubType(): string {
    return this.widgetSubType;
  }

  /**
   * Update the initState with the default property values and the values provided in the markup
   * Process the attributes
   * Register the widget
   */
  protected initWidget() {
    this.initState = new Map<string, any>();
    // get the widget properties
    let widgetProps: { [key: string]: any } = getWidgetPropsByType(this.customWidgetSubType ? this.customWidgetSubType : this.widgetSubType);

    if (this.widget.widgetProps) {
      // this occurs when widget has been modified and compiled and after appending new compiled element, widgetProps are lost.
      // observed incase of prefab.
      widgetProps = extend(widgetProps, this.widget.widgetProps);
    }
    if (this.widgetType === 'wm-form-field' || this.widgetType === 'wm-filter-field') {
      // this extra step, will reset some widgetProps, for suppose, dataset is empty for formfield (radioset) but has default dataset for widgets
      widgetProps = (this as any).getFormWidgetProps(this.customWidgetSubType ? this.customWidgetSubType : undefined);
      if(this.widgetSubType === 'wm-form-field-custom-widget') {
        let customWidgetName = this.widget.formWidget.nativeElement.widget.widgetName;
        widgetProps = extend(widgetProps, getWidgetPropsByType(customWidgetName));
      }
    } else if (this.widgetType === 'wm-checkbox') {
      const checkboxType = this.nativeElement.attributes.getNamedItem("type")?.value
      if (checkboxType === 'button') {
          widgetProps = getWidgetPropsByType('wm-iconbutton-toggle');
      }else if(checkboxType === 'toggle'){
          widgetProps = getWidgetPropsByType('wm-toggle');
      }
    }
      if(this.nativeElement.attributes.getNamedItem("is-range")?.value==='true') {
          widgetProps['maxplaceholder'].value=widgetProps['placeholder'].value;
      }
    forEach(widgetProps, (v: any, k: string) => {
      if (v && isDefined(v.value)) {
        this.initState?.set(k, v.value);
      } else if (this.widgetType === 'wm-prefab' && isDefined(v.__value)) {
        this.initState?.set(k, v.__value);
      }
    });
    if (this.widgetType === 'wm-text') {
      if (this.$element.closest('[captionposition="floating"]').length) {
        this.initState?.set('placeholder', '');
      }
      this.updateAttrsOnInput();
    }
    this.widgetProps = widgetProps;
    this.processAttrs();

    this.registerWidget(this.initState.get('name'));
  }

  // For input widget ie. wm-text with types, updating the placeholder and type of input.
  updateAttrsOnInput(placeholderVal?: string) {
    // Not setting the placeholder attribute on the input when it has caption floating set to the element. (As label will be shown instead of placeholder)
    if (this.$element.find('input').closest('.caption-floating').length || this.$element.closest('[captionposition="floating"]').length) {
      this.$element.find('input').first().attr('type', placeholderVal || this.initState?.get('placeholder'));
      return;
    }
      if (this.$element.is('input') || this.$element.is('textarea')) {
        setAttr(this.nativeElement, 'placeholder', placeholderVal || this.initState?.get('placeholder'));
      } else {
        this.$element.find('input').first().attr('placeholder', placeholderVal ?? this.initState?.get('placeholder'));
        this.$element.find('input').first().attr('type', placeholderVal || this.initState?.get('placeholder'));
      }
  }

  private processPrefabWidget() {
    let $el = $(this.nativeElement);
    let isInput = $el.is('input, textarea, select');
    let isButton = $el.is('button');
    let isInputWrapper = $el.is('wm-input, wm-textarea, wm-select, div[wmcolorpicker]');

    if ((!isInput && !isButton && !isInputWrapper) || isInputWrapper) {
      const childEl = $el[0].firstChild;
      const attrVal: string | undefined =  $el.attr('prefab-widget-id');
      if (childEl && attrVal) {
        $(childEl).attr('prefab-widget-id', attrVal);
      }
    }
  }

  // This method adds readonly to the input widgets on canvas.
  protected processWidget() {
    let $el = $(this.nativeElement);
    let isInput = $el.is('input, textarea, select');
    let isButton = $el.is('button');
    let isInputWrapper = $el.is('wm-input, wm-textarea, wm-select, div[wmcolorpicker]');

    if (isInput || isInputWrapper) {
      $el.attr('readonly', 'true');
      $el.removeAttr('disabled');
    }
    if ($el.attr('formcontrolname')) {
      $el.find('input, textarea, select').attr('readonly', 'true');
      $el.attr('readonly', 'true');
    }

    if (isButton) {
      $el.removeAttr('disabled');
    }

    if (!this.$attrs.get('is-container') && !isInput) {
      $el.append('<div class="wm-widget-overlay-inner"></div>');
    }
  }

  protected initContext() {
    const context = this.context;

    const parentContexts = this.inj.get(Context, {});

    // assign the context property accordingly
    if (this.viewParent !== context) {
      this.context = context;
    } else {
      this.context = {};
    }

    if (parentContexts) {
      let parentContextObj: any = {};
      if (isArray(parentContexts)) {
        forEach(parentContexts, (contextObj) => {
          Object.assign(parentContextObj, contextObj);
        });
      } else {
        parentContextObj = parentContexts;
      }
      this.context = Object.assign({}, parentContextObj, this.context);
    }
  }

  // Process the attributes
  private processAttrs() {
    const attrs = this.nativeElement.attributes;
    let i = 0;
    map(attrs, (attr: Attr) => {
      this.$attrs.set(attr.name, attr.value);
      this.processAttr(attr.name, attr.value);
    });
  }

  /**
   * Process the attribute
   * If the attribute is an event expression, generate a functional representation of the expression
   *      and keep in eventHandlers
   * If the attribute is a bound expression, register a watch on the expression
   */
  protected processAttr(attrName: string, attrValue: string) {
    const { 0: propName, 1: type, 2: meta, length } = attrName.split('.');
    if (type === 'bind') {
      // removed code for setting 'hidden' attribute on native element as it is not required
      this.processBindAttr(propName, attrValue);
    } else if (type === 'event') {
      this.processEventAttr(propName, attrValue, meta);
    } else if (length === 1) {
      // remove class and name attributes. Component will set them on the proper node
      if (attrName === 'class') {
        removeClass(this.nativeElement, attrValue);
      } else if (attrName === 'tabindex') {
        // if (!this.widget.name) {
          // // if widget is already present and new data is compiled again, then we need not remove the name that was previously added.
          removeAttr(this.nativeElement, attrName);
        // }
      }

      this.initState?.set(propName, attrValue);
    } else {
      // custom attributes provided on elDef;
    }
  }

  /**
   * parse the event expression and save reference to the function inside eventHandlers map
   * If the component provides a override for an event through @Event decorator invoke that
   * else invoke the resolved function
   *
   * @param {string} eventName
   * @param {string} expr
   */
  protected processEventAttr(eventName: string, expr: string, meta?: string) {
    const fn = $parseEvent(expr);
    const locals = this.context;
    locals.widget = this.widget;
    const boundFn = fn.bind(undefined, this.viewParent, locals);

    const eventCallback = () => {
      let boundFnVal;
      $invokeWatchers(true);
      try {
        locals.$event = locals.$event || new window.CustomEvent(eventName);
        // If the event is bound directly to the variable then we need to internally handle
        // the promise returned by the variable call.
        boundFnVal = boundFn();
        if (boundFnVal instanceof Promise) {
          boundFnVal.then(response => response, err => err);
        } else {
          return boundFnVal;
        }
      } catch (e) {
        console.error(e);
      }
    };

    this.eventHandlers.set(this.getMappedEventName(eventName), { callback: eventCallback, locals });
    // prepend eventName with on and convert it to camelcase.
    // eg, "click" ---> onClick
    const onEventName = camelCase(`on-${eventName}`);
    // save the eventCallback in widgetScope.
    set(this, onEventName, expr);

    // events needs to be setup after viewInit
    this.toBeSetupEventsQueue.push(() => {
      this.handleEvent(this.nativeElement, this.getMappedEventName(eventName), eventCallback, locals, meta);
    });
  }

  protected getMappedEventName(eventName: string) {
    return EVENTS_MAP.get(eventName) || eventName;
  }

  /**
   * invoke the event handler
   * Components can override this method to execute custom logic before invoking the user callback
   */
  protected handleEvent(node: HTMLElement, eventName: string, eventCallback: Function, locals: any, meta?: string) {
    this.eventManager.addEventListener(
      node,
      eventName,
      (e: any) => {
        locals.$event = e;
        if (meta === 'delayed') {
          setTimeout(() => eventCallback(), 150);
        } else {
          return eventCallback();
        }
      }
    );
  }

  processBindInfo(expr: string, propName: string) {
    let bindInfo = get(this.widgetProps[propName], 'bindInfo');
    if (bindInfo && (bindInfo.evaulateBindings || bindInfo.handleEmptyEvaluation)) {
      setAttr(this.nativeElement, 'data-evaluated-' + propName, '');
      this.widget[propName] = this.widget.name;
      if (bindInfo.invokeVariable && !(expr.startsWith('bind:Widgets.') || expr.startsWith('Widgets.'))) {
        let boundExpr = expr.replace('bind:', '');
        const dataSource = getDataSource(boundExpr);
        if (dataSource) {
          this.processBindAttr('datasource', dataSource);
          this.processDataSource(expr);
        }
      }
      this.setWatcher(expr, propName);
    }
  }


  /**
   * Process the bound property
   * Register a watch on the bound expression
   */
  protected processBindAttr(propName: string, expr: string) {

    this.initState?.delete(propName);
    if (propName === 'show') {
      set(this.widgetProps, 'deferload.show', true);
    }
    if (this.isBoundToWidgetSelectedItem(expr, propName)) {
      return;
    }
    if ((this.widget.viewParent?.prefabName || this.widget.prefabName) && propName !== 'show') {
      if (propName !== 'caption' && propName !== 'title') {
        this.evaluateBindExpr(propName, expr);
      }
      return;
    } else if (propName === 'show' && this.isClosestPrefab(this.widget)) {
      this.evaluateBindExpr(propName, expr);
      return;
    }

    if (this.isCustomWidget()) {
        this.evaluateBindExpr(propName, expr);
        // return;
    }

    this.processBindInfo(expr, propName);
  }

  isBoundToWidgetSelectedItem(expr: string, propName: string) {
    let currentItemRegEx = /^bind:Widgets\..*\.currentItem([\.]?)/g;
    let selectedItemRegEx = /^bind:Widgets\..*\.selecteditem([\.]?)/g;

    expr = expr.startsWith('bind:') ? expr : 'bind:' + expr;

    if (currentItemRegEx.test(expr) || selectedItemRegEx.test(expr)) {
      this.initState?.set(propName, expr);
      return true;
    }
    return false;
  }

  isClosestPrefab(widget: any): boolean {
    if (!widget.viewParent) {
      return false;
    }
    if (widget.viewParent?.prefabName) {
      return true;
    }
    return this.isClosestPrefab(widget.viewParent);
  }

  /**
   * Register the widget with the widgetRegistry
   */
  protected registerWidget(widgetName: string) {
    // if (this.widgetId) {
      this.registerDestroyListener(register(this.widget, this.viewParent, this.pageName, this.widgetId, widgetName));
    // }
  }

  public registerStyleChangeListener(fn: ChangeListener, ctx?: any) {
    if (ctx) {
      fn = fn.bind(ctx);
    }
    // @ts-ignore
    this.styleChange.subscribe(({ key, nv, ov }) => fn(key, nv, ov));
  }

  /**
   * Handles the common functionality across the components
   * eg,
   *  1. value of the class property will be applied on the host element
   *  2. based on the value of show property component is shown/hidden
   *
   * @param {string} key
   * @param nv
   * @param ov
   */
  protected onPropertyChange(key: string, nv: any, ov?: any) {
    const excluded = ['wm-text', 'wm-button'];
    if (key === 'show') {
      this.nativeElement.hidden = !nv;
      if (get(this.widgetProps, 'deferload.show') == true && nv == true)
      set(this.widgetProps, 'deferload.show', false);
    } else if (key === 'hint') {
      if (!isMobileApp()) {
        setAttr(this.nativeElement, 'title', nv);
      }
    } else if (key === 'class') {
      switchClass(this.nativeElement, nv, ov);
      let result = nv.match(/(\W|^)(h([0-6]))(?=\s|$)/);
      if (result) {
        setAttr(this.nativeElement, 'role', 'heading');
        setAttr(this.nativeElement, 'aria-level', result[3]);
      }
    } else if (key === 'name' || key === 'tabindex') {
      setAttr(this.nativeElement, key, nv);
      if (key === 'name' && nv && this._initialized) {
        renameWidget(this.viewParent, this.widget, nv, ov);
      }
    } else if (key === 'conditionalclass') {
      // update classes if old and nv value are different
      updateClasses(nv.toAdd, nv.toRemove, this.nativeElement);
    } else if (key === 'autoplay') {
      const tagName = this.widgetType === 'wm-audio' ? 'audio' : 'video';
      // Trigger media(audio/video) element load method after changing autoplay property
      // this.nativeElement.querySelector(tagName)?.load();
    } else if (key === 'conditionalstyle') {
      // update styles if old and nv value are different
      updateStyles(nv, ov, this.nativeElement);
    } else if (key === 'animation') {
      // update styles if old and nv value are different
      addClass(this.nativeElement, "animated " + nv);
    } else if (key === 'disabled' && !excluded.includes(this.widgetType)) {
      // WMS-19321: In IE, widget is in disabled state when disabled="false" attribute is present
      (nv === true) ? setAttr(this.nativeElement, 'disabled', 'true', true) : removeAttr(this.nativeElement, 'disabled', true);
    } else if (key === 'placeholder') {
      this.updateAttrsOnInput(nv);
    }
    // On binding the prop value to some variable / widget.
    if (isDefined(nv) && isString(nv) && nv.startsWith('bind:')) {
      this.processBindAttr(key, nv);
    }
  }

  updateVariantOptions(): void {
    const fileName = this.tokenFileName || '';
    if (!fileName) {
      set(this.widgetProps, 'variant.show', false);
      return;
    };
    this.widgetStylingService.extractAppearanceVariantMap(fileName).then((appearanceMap: any) => {
      // Build variant list as "appearance:variant"
      const variantList = Object.entries(appearanceMap || {}).flatMap(
        ([appearance, value]: [string, any]) => {
          const variants = Object.keys(value?.variants || {});
          if (variants.length === 0) {
            return [
              {
                name: TOKENS_KEYS.STANDARD,
                value: `${appearance}`,
                category: appearance,
                custom: false, 
              }
            ];
          }
          return variants.map(variantName => ({
            name: variantName,
            value: appearance === 'custom'
              ? variantName
              : `${appearance}:${variantName}`,
            category: appearance,
            custom: appearance === 'custom'
          }));
        }
      );    
      // Show variants if available
      const showVariant = variantList.length > 0;
      set(this.widgetProps, 'variant.show', showVariant);
      if (showVariant) {
        set(this.widgetProps, 'variant.options', variantList);
      }
    });
  }

  protected syncAppearanceVariantClasses(nv: string, ov?: string, custom? : boolean): void {
    let [newAppearance, newVariant] = nv.split(':');

    if (custom) {
      newVariant = newAppearance
      newAppearance = "custom"; 
    } else if (!custom && !newVariant) {
      newVariant = "";
    }
    let [prevAppearance, prevVariant] = ov?.split?.(':') || ['', ''];

    const fileName = this.tokenFileName || '';
    const defaultCls = this.widget.DEFAULT_CLS;
    // Get previous token classes from cache

    const prevTokenClasses = prevAppearance
  ? [
      this.widgetStylingService.getVariantSelectorFromCache(fileName, prevAppearance, prevVariant),
      this.widgetStylingService.getVariantSelectorFromCache(fileName, "custom", prevAppearance),
      this.widgetStylingService.getVariantSelectorFromCache(fileName, prevAppearance, "")
    ].find(arr => Array.isArray(arr) && arr.length > 0) ?? []
  : [];


    // Get new token classes from cache
    const newTokenClasses = newAppearance
      ? this.widgetStylingService.getVariantSelectorFromCache(fileName, newAppearance, newVariant) || []
      : [];

    // Extract widget's current classes
    const widgetClassList = (get(this.widget, 'class') || '')
      .split(' ')
      .filter((cls: string) => cls.trim() !== '');

    // Remove previous appearance/variant classes
    const filteredClasses = widgetClassList.filter(
      (cls: string) => !prevTokenClasses.includes(cls) && cls !== defaultCls
    );

    // filtering newTokenClasses to avoid adding defaultCls
    const filteredNewTokenClasses = newTokenClasses.filter((cls: string) => cls !== defaultCls);

    // Add new appearance/variant classes, avoiding duplicates
    const updatedClassList = Array.from(new Set([...filteredClasses, ...filteredNewTokenClasses]));
    const finalClassStr = updatedClassList.join(' ');

    // Update class on widget
    this.widget.class = finalClassStr;
    set(this.widgetProps, 'class.value', finalClassStr);

    //Ensuring the widget default class is re-applied
    defaultCls && addClass(this.nativeElement, defaultCls);

    // To handle buttongroup variant in mobile
    if (this.widgetType === 'wm-buttongroup') {
      const isVertical = newVariant === 'vertical' ? "true" : "false"
      runtimeEventManager.emit('set-markup-attr', {
        widgetId: this.widgetId,
        'vertical': isVertical
      });
    }

    // To handle message variant in mobile
    if (this.widgetType === 'wm-message') {
      runtimeEventManager.emit('set-markup-attr', {
        widgetId: this.widgetId,
        'type': newVariant
      });
    }

    // Reflect updated class in markup
    runtimeEventManager.emit('set-markup-attr', {
      widgetId: this.widgetId,
      class: finalClassStr
    });
    runtimeEventManager.emit('updateShadowProperty');
  }

  updatePageStructure() {
    if (PAGE_STRUCTURE_MAP_FOR_WIDGETS.get(this.widgetType)) {
      this.registerPropertyChangeListener((key: string, nv: any, ov: any) => {
        if (PAGE_STRUCTURE_MAP_FOR_WIDGETS.get(this.widgetType)?.includes(key)) {
          runtimeEventManager.emit('update-pagestructure');
        }
      })
    }
  }

  /**
   * Generates a unique id
   * Default pattern is `widget-id-${id}`
   * Components can override this method to generate a different id eg, bar-chart-1
   */
  protected generateWidgetId(): string {
    return widgetIdGenerator.nextUid();
  }

  // Default style change handler
  protected onStyleChange(k: string, nv: any, ov?: any) { }

  public registerPropertyChangeListener(fn: ChangeListener, ctx?: any) {
    if (ctx) {
      fn = fn.bind(ctx);
    }
    // @ts-ignore
    this.propertyChange.subscribe(({ key, nv, ov }) => fn(key, nv, ov));
  }

  processBindedVal(attr: string, expr?:string) {
    if (attr.endsWith('.bind')) {
      let val = expr || 'bind:' + this.$attrs.get(attr);
      attr = attr.replace('.bind', '');
      this.widget['bind' + attr] = val;
      // leave picturesource type of attributes as it is. It will be evaluated in the component
      if (isDefined(this.widget['bind' + attr]) && attr !== 'picturesource' && attr !== 'dataset') {
        if (attr === 'caption' || attr === 'title' || attr === 'displayname') {
          if (this.widget.name) {
            // Retaining the label caption value in custom widgets when switching workspace
            if (!this.isCustomWidget()){
              this.widget[attr] = this.widget.name;
            }
            if (this.widgetType === 'wm-tabpane') {
              // adding bindingicon to tabpane title by adding data-evaluated-title to tabs.
              let paneId = this.widgetId;
              let tabsEl = this.widget.viewParent.nativeElement;
              let existingpaneId = tabsEl.getAttribute('data-evaluated-' + attr) || '';
              tabsEl.setAttribute('data-evaluated-' + attr, existingpaneId ? existingpaneId + ' ' + paneId : paneId);
            } else {
              setAttr(this.nativeElement, 'data-evaluated-' + attr, '');
            }
          } else if (this.nativeElement?.parentElement?.hasAttribute('data-col-identifier')) {
            // This case is specifically for viewmode widget rendering in datatable
            this.widget[attr] = this.widgetType.replace('wm-', '');
            // if widgetid is also not there, binding icon will not be shown.
            if (!this.nativeElement.getAttribute('widgetid')) {
              this.nativeElement.setAttribute('widgetid', '');
            }
          }
        }
      }
    }
  }

  // In studio, when bindcaption prop is added, caption has to be assigned to its name itself
  // TODO: add binding logic here.
  private assignBindableProps() {
    for (let attr of this.$attrs.keys()) {
      this.processBindedVal(attr);
    }
  }

  /**
   * Update the default properties and the properties provided in the markup in component
   * Invoking this method will result in invocation of propertyChange handlers on the component for the first time
   */
  protected setInitProps() {
    if (this.initState?.get('name')) {
      this.widget.name = this.initState.get('name');
      this.assignBindableProps();
    }
    this.initState?.forEach((v, k) => {
      let updateKey;
      if (MapWidgetProp.hasOwnProperty(k)) {
        updateKey = get(MapWidgetProp, k);
      }
      // name is already set, ignore name
      // if the key is part of to be ignored attributes list do not set it on the component instance
      if ((this.widgetProps[k] || isStyle(k)) && k !== 'name') {
        if (updateKey) {
          this.widget[updateKey] = v
        } else {
          this.widget[k] = v;
        }
      }
    });
     // apply appearance and variant attributes from the markup to the widget instance.
    if (this.nativeElement && this.widget && this.widgetProps && this.widgetProps['variant']) {
      this.updateVariantOptions();
    }
    this.initState?.clear();
    this.initState = undefined;

    this.readyState.next(null);
    this.readyState.complete();
  }

  public registerDestroyListener(fn: Function, ctx?: any) {
    if (ctx) {
      fn = fn.bind(ctx);
    }
    this.destroy.subscribe(() => { }, () => { }, () => fn());
  }

  /**
   * nativeElement will be available by this time
   * if the delayInit is false, properties meta will be available by this time
   * Invoke the setInitProps if the delayInit is false
   */
  ngOnInit() {
   if (!this.delayedInit) {
      this.setInitProps();
    }
  }

  ngAfterViewInit() {
    // When widgets rendered inside partial, which is rendered on the page, dotted border for partial widgets should not be visible
    if (this.$element.closest('.app-included-page').length) {
      const elementsWithAttr: any = this.nativeElement.querySelectorAll('[widgetid]');
      const registry = getWidgetRegistry();
      elementsWithAttr.forEach((el: any) => {
        const widgetId = el.getAttribute('widget-id');
        registry.delete(widgetId);
        el.removeAttribute('widgetid');
        el.removeAttribute('widget-id');
      });
    }
    if (this.nativeElement.hasAttribute('widget-id') || this.nativeElement.hasAttribute('widgetid')) {
      this.processWidget();
    }
    if (this.nativeElement.hasAttribute('prefab-widget-id')) {
      this.processPrefabWidget();
    }
  }

  public notifyStyleChange(key: string, nv: any, ov: any) {
    this.styleChange.next({ key, nv, ov });
  }

  public notifyPropertyChange(key: string, nv: any, ov: any) {
    this.propertyChange.next({ key, nv, ov });
  }

  /**
 * invoke the event callback method
 * @param {string} eventName
 * @param extraLocals
 */
  public invokeEventCallback(eventName: string, extraLocals?: any) {
    const callbackInfo = this.eventHandlers.get(eventName);
    if (callbackInfo) {
      const fn = callbackInfo.callback;
      const locals = callbackInfo.locals || {};

      if (fn) {
        return fn(Object.assign(locals, extraLocals));
      }
    }
  }

  /**
 * Remove watch on the bound property
 */
  protected removePropertyBinding(propName: string) {
    $unwatch(getWatchIdentifier(this.widgetId, propName));
  }

  public registerReadyStateListener(fn: Function, ctx?: any) {
    if (ctx) {
      fn = fn.bind(ctx);
    }
    if (this.readyState.isStopped) {
      fn();
      return;
    }
    this.readyState.subscribe(() => fn());
  }

  isBindableProperty(propDetails: any) {
    return propDetails.bindable === 'in-bound' || propDetails.bindable === 'in-out-bound';
  }

  evaluateBindExpr(propName: string, expr: string) {
    let widgetID = this.widgetId || this.generateWidgetId();
    expr = expr.startsWith('bind:') ? expr.replace('bind:', '') : expr;
    this.registerDestroyListener(
      $watch(
        expr,
        this.viewParent,
        this.context,
        (nv: any) => this.widget[propName] = nv,
        getWatchIdentifier(widgetID, propName),
        propName === 'datasource',
        this.widgetProps[propName],
        () => false
      ));
    setTimeout(() => {
      $invokeWatchers(true);
    }, 50);
  };

  setWatcher(expr: string, propName: string) {
    expr = expr.startsWith('bind:') ? expr.replace('bind:', '') : expr;
    let widgetID = this.widgetId || this.generateWidgetId();
    let widgetScopes: any;
    if (expr.startsWith('Widgets.') && (!(this.viewParent as any)?.Widgets && !(this.context as any)?.Widgets)) {
      let getWidgetsByType = runtimeEventManager.getService('getWidgetsByType');
      // fetch the list of widgets
      widgetScopes = getWidgetsByType('form-widgets');
      set((this.viewParent as any), 'Widgets', widgetScopes);
      setTimeout(() => {
        $invokeWatchers(true);
      }, 50);
    }

    this.registerDestroyListener(
      $watch(
        expr,
        this.viewParent,
        this.context,
        (nv: any) => {
          let bindInfo = this.widgetProps[propName].bindInfo;
          if (bindInfo && !this.isCustomWidget()) {
            if (bindInfo.handleEmptyEvaluation) {
              if (!isDefined(nv) || !nv) {
                setAttr(this.nativeElement, 'data-evaluated-' + propName, '');
                this.widget[propName] = this.widget.name;
                return;
              }
            }
            removeAttr(this.nativeElement, 'data-evaluated-' + propName);
            this.widget[propName] = nv;
          }
        },
        getWatchIdentifier(widgetID, propName),
        propName === 'datasource',
        this.widgetProps[propName],
        () => this.isMuted
      )
    );
  }

  processDataSource(val: string) {
    let varInstance: any;
    let prefabName: string | undefined = get((this as any), 'prefabName');
    let partialName: string | undefined = get((this.viewParent as any), 'partialName');
    if (!prefabName) {
      let isInsidePrefab = $(this.nativeElement).closest('[wmprefab]');
      if (isInsidePrefab.length) {
        let pfScope = get(isInsidePrefab[0], 'widget');
        prefabName = get(pfScope, 'prefabName');
      }
    }
    if (prefabName) {
      varInstance = this.baseVariableService.getVariableFromNameSpace(prefabName);
    } else {
      // if widget is rendered inside partial, get variable instance from partial
      varInstance = this.baseVariableService.getVariableInstance(val, partialName);
    }

    if (varInstance) {
      this.widget['datasource'] = varInstance;
      let uniqueDatasourceID = varInstance.package;
      if (isFunction(varInstance.invoke) && uniqueDatasourceID) {
        this.baseVariableService.invokeDataset(varInstance).then((response: any)=> {
          varInstance.dataSet = response.data;
          this.widget['dataset'] = response.data;
          $invokeWatchers();
        });
      } else {
        if (!uniqueDatasourceID) {
          setAttr(this.nativeElement, 'data-evaluated-dataset', '');
        }
        this.widget['dataset'] = varInstance.dataSet;
        $invokeWatchers();
      }
    }
  }

  // Function to check if the widget is a custom widget
  isCustomWidget(): boolean {
    return Boolean(this.widget.viewParent?.customWidgetName || this.widget.customWidgetName);
  }


  ngOnDestroy() {
    this.widget = Object.create(null);
    this.styleChange.complete();
    this.propertyChange.complete();
    this.destroy.complete();
  }
}
